import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import BigNumber from 'bignumber.js';
import { getValidChain, OLstvToken } from 'modules/api';
import { useConnection } from 'modules/auth';
import { SpriteIcon } from 'modules/common/components/SpriteIcon';
import { lbtcSpriteIcon } from 'modules/common/icons';
import { useTranslation } from 'modules/i18n';
import { useWithdrawalForm } from '../../hooks/useWithdrawalForm';
import { WithdrawalInProgress } from '../WithdrawalInProgress';
import { WithdrawalSummary } from '../WithdrawalSummary';
import { AmountField } from './AmountField';
import { translation } from './translation';
import { WithdrawalInfo } from './WithdrawalInfo';
import { useGetWithdrawalStatusQuery } from 'modules/dashboard/actions/getWithdrawalStatus';

const WITHDRAWAL_STATUS_POLLING_INTERVAL = 10_000; // 10 seconds

export function WithdrawalForm(): JSX.Element {
  const { address, chainId } = useConnection();
  const { keys, t } = useTranslation(translation);
  const { form, isLoading: isLoadingForm, onSubmit } = useWithdrawalForm();
  const amount = form.watch('amount');

  const { isLoading: isLoadingStatus } = useGetWithdrawalStatusQuery(
    { address, chainId: getValidChain(chainId) },
    {
      pollingInterval: WITHDRAWAL_STATUS_POLLING_INTERVAL,
      skip: !address,
    },
  );

  const isLoading = isLoadingForm || isLoadingStatus;

  return (
    <WithdrawalInProgress>
      <Stack component="form" onSubmit={onSubmit} gap={3}>
        <AmountField
          {...form}
          startAdornment={
            <Stack direction="row" alignItems="center" gap={1}>
              <SpriteIcon
                src={lbtcSpriteIcon}
                sx={{
                  width: 27,
                  height: 27,
                }}
              />

              <Typography
                color="text.primary"
                fontWeight="500"
                fontSize={{
                  xs: 10,
                  sm: 24,
                }}
              >
                {OLstvToken.sonicLBTCv}
              </Typography>
            </Stack>
          }
        />

        <WithdrawalSummary amount={new BigNumber(amount).toNumber()} />

        <Stack gap={2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            type="submit"
            sx={{
              height: 48,
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              t(keys.approveWithdrawal)
            )}
          </Button>

          <WithdrawalInfo amount={amount} />

          <Alert severity="warning">{t(keys.quote)}</Alert>
        </Stack>
      </Stack>
    </WithdrawalInProgress>
  );
}
